import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import ProgressBar from '@repo/ui/progress-bar'
import Typography from '@repo/ui/typography'
import { useEffect, useMemo, useRef } from 'react'

import ArrowLeftOneIcon from '../../../../../../../packages/ui/icons/arrow-left-01'
import ArrowRightOneIcon from '../../../../../../../packages/ui/icons/arrow-right-01'
import useLanguageTest from '../../../../views/client-personal-space/informations/context/use-language-test'
import LanguageTestQuestions from './language-test-questions'
import LanguageTestTimer from './language-test-timer'

const LanguageTestSections = () => {
  const testTopRef = useRef<HTMLDivElement>(null)
  const {
    responses,
    currentActiveSubmission,
    nextButtonLoading,
    previousButtonLoading,
    isFinalizingSubmission,
    missingQuestions,
    handleNextButtonClick,
    handlePreviousButtonClick
  } = useLanguageTest()

  const { progressValue } = useMemo(() => {
    const sections = currentActiveSubmission?.testTemplate?.sections
    if (!sections)
      return { answeredQuestionsCount: responses.size, totalQuestionsCount: 0, progressValue: 0 }

    let answeredQuestionsCount = 0
    let totalQuestionsCount = 0
    sections.forEach((section) => {
      totalQuestionsCount += section.questions.length
      answeredQuestionsCount += section.questions.filter((q) => responses.has(q.id)).length
    })

    return {
      answeredQuestionsCount,
      totalQuestionsCount,
      progressValue:
        totalQuestionsCount === 0 ? 0 : (answeredQuestionsCount / totalQuestionsCount) * 100
    }
  }, [currentActiveSubmission?.testTemplate?.sections, responses])

  const haveMissingQuestions = missingQuestions && missingQuestions.length > 0

  useEffect(() => {
    if (!currentActiveSubmission?.currentSectionId) return

    testTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [currentActiveSubmission?.currentSectionId])

  // Scroll to top when the missing-questions view appears (clicking "Soumettre" on
  // the last section) — the section id doesn't change here, so the effect above
  // wouldn't fire and the user would stay at the bottom of the page.
  useEffect(() => {
    if (!haveMissingQuestions) return

    testTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [haveMissingQuestions])

  return (
    <div ref={testTopRef}>
      <Box className="space-y-4 pt-2 mobile:px-0 mobile:pb-0">
        <div className="flex flex-col gap-4 py-4 px-1 sticky top-0 mobile:-top-4 bg-white z-10">
          {/* Header + Timer */}
          <div className="flex items-center justify-between w-full">
            <Typography variant="h3" color={tailwindColors.primary.dark.DEFAULT}>
              {haveMissingQuestions
                ? 'Questions manquantes'
                : currentActiveSubmission?.currentSection.label}
            </Typography>
            <LanguageTestTimer />
          </div>

          {/* Progress Bar */}
          <div className="rounded-xl bg-white-hover p-4 flex flex-col gap-4">
            <ProgressBar
              text={'de vos informations sont complétées'}
              progress={Number(progressValue.toFixed(0))}
              colorStart={tailwindColors.fushia.active}
              colorEnd={tailwindColors.primary.DEFAULT}
              animationMs={350}
            />
          </div>
        </div>

        {/* Questions */}
        <LanguageTestQuestions
          subTitle={
            haveMissingQuestions ? (
              <div className="flex flex-col gap-4">
                <div className="rounded p-4 bg-[#EAF5F9]">
                  <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
                    Il semble que vous ayez laissé quelques questions en suspens. N'hésitez pas à
                    les laisser vides si vous n'êtes pas sûr(e) de la réponse. Vous pouvez
                    soumettre votre test ou prendre un moment pour y réfléchir à nouveau.
                  </Typography>
                </div>

                <Typography variant="text">Questions</Typography>
              </div>
            ) : (
              'Choisissez la bonne réponse parmi les 4 réponses possibles (une seule réponse).'
            )
          }
          questions={
            haveMissingQuestions
              ? (missingQuestions ?? [])
              : (currentActiveSubmission?.currentSection.questions ?? [])
          }
        />

        {/* Navigation */}
        <div className="flex justify-end mobile:justify-center items-center pt-6 gap-4">
          <Button
            variant="outline-black"
            onClick={handlePreviousButtonClick}
            disabled={
              !haveMissingQuestions &&
              (!currentActiveSubmission?.canGoPrevious || isFinalizingSubmission)
            }
            loading={previousButtonLoading}
            label="Précédent"
            className="mobile:w-full"
            icon={ArrowLeftOneIcon}
          />

          <Button
            onClick={handleNextButtonClick}
            className="mobile:w-full"
            variant={currentActiveSubmission?.canGoNext ? 'ghost' : 'primary'}
            disabled={isFinalizingSubmission}
            loading={nextButtonLoading}
            label={currentActiveSubmission?.canGoNext ? 'Suivant' : 'Soumettre'}
            icon={currentActiveSubmission?.canGoNext ? ArrowRightOneIcon : undefined}
            iconToTheRight
          />
        </div>
      </Box>
    </div>
  )
}

export default LanguageTestSections
